import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { ChatAuthor } from '../../lib/types';

/**
 * The menu that opens when you click a name in chat.
 *
 * Every social action in the game reachable from wherever a player is
 * mentioned — that is what makes chat a hub rather than a box of text. The
 * destructive ones (report, block) are separated below a rule so they are never
 * the thing you hit reaching for "visit profile".
 */
export function PlayerMenu({
  author,
  isSelf,
  position,
  canInvite,
  onClose,
  onBlock,
  onReport,
}: {
  author: ChatAuthor;
  isSelf: boolean;
  position: { left: number; top: number };
  /** True when the viewer is an officer of a guild with room to spare. */
  canInvite: boolean;
  onClose: () => void;
  onBlock: () => void;
  onReport: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Focus moves into the menu so a keyboard user is not left behind on the
  // name they just activated.
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('a,button')?.focus();
  }, []);

  return (
    <>
      <div className="chat-menu__scrim" onClick={onClose} />
      <div
        ref={ref}
        className="pxframe pxframe--raised chat-menu"
        style={{ left: position.left, top: position.top }}
        role="menu"
        aria-label={`Actions for ${author.username}`}
      >
        <div className="chat-menu__head">
          <span className="mono">{author.trainerName}</span>
          <span className="tiny muted">
            @{author.username} · Lv {author.level}
          </span>
          {author.isNpc && (
            <span className="chip chip--npc chip--sm" title="Game-controlled trainer">
              🌿 NPC
            </span>
          )}
        </div>

        <Link className="chat-menu__item" to={`/trainer/${author.username}`} onClick={onClose}>
          ◓ Visit Profile
        </Link>
        <Link className="chat-menu__item" to={`/grove/${author.username}`} onClick={onClose}>
          🌿 Visit Grove
        </Link>
        <Link className="chat-menu__item" to={`/bazaar/${author.username}`} onClick={onClose}>
          ▤ Visit Bazaar
        </Link>

        {!isSelf && !author.isNpc && (
          <>
            <Link
              className="chat-menu__item"
              to={`/friends?add=${author.username}`}
              onClick={onClose}
            >
              ♥ Friend Request
            </Link>
            <Link
              className="chat-menu__item"
              to={`/trades?with=${author.username}`}
              onClick={onClose}
            >
              ⇄ Offer a Trade
            </Link>
            {canInvite && (
              <Link
                className="chat-menu__item"
                to={`/guilds?invite=${author.username}`}
                onClick={onClose}
              >
                🏰 Invite to Guild
              </Link>
            )}
            <button className="chat-menu__item" type="button" disabled>
              ✉ Message <span className="tiny muted">(soon)</span>
            </button>
          </>
        )}

        {!isSelf && !author.isNpc && (
          <>
            <hr className="divider" />
            <button
              className="chat-menu__item chat-menu__item--warn"
              type="button"
              onClick={() => {
                onReport();
                onClose();
              }}
            >
              ⚑ Report
            </button>
            <button
              className="chat-menu__item chat-menu__item--warn"
              type="button"
              onClick={() => {
                onBlock();
                onClose();
              }}
            >
              ⊘ Block
            </button>
          </>
        )}
      </div>
    </>
  );
}
